import {otsBaseApi} from "../../ots-api";
import {CompanySettlement, CompanySettlementMapTtasCriteria} from "../../../model/company-settlement";
import {TtaMappedPair} from "../../../model/tta-mapped-pair";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ['CompanySettlements', 'SettlementCompanySettlements']})

const companySettlementApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllCompanySettlements: builder.query<CompanySettlement[], string | undefined>({
      query: (regionId) => ({
        url: `/company-settlements/all`,
        method: 'GET',
        params: regionId ? {regionId: regionId} : undefined
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: 'CompanySettlements', id} as const)),
            {type: 'CompanySettlements', id: 'LIST'},
          ];
        } else {
          return [{type: 'CompanySettlements', id: 'LIST'}];
        }
      },
    }),
    getCompanySettlementsBySettlement: builder.query<
      CompanySettlement[], string
    >({
      query: (arg) => ({
        url: `/company-settlements/settlement/${arg}`,
        method: 'GET',
      }),
      providesTags: (result, _error, arg, _meta) => {
        if (result) {
          return [
            ...result.map(
              ({id}) => ({type: "CompanySettlements", id} as const)
            ),
            {type: "SettlementCompanySettlements", id: arg}
          ];
        } else {
          return [];
        }
      },
    }),
    getCompanySettlement: builder.query<CompanySettlement, string>({
      query: (id) => ({
        url: `/company-settlements/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{type: 'CompanySettlements', id}],
    }),
    mapCompanySettlementTtas: builder.query<TtaMappedPair[], CompanySettlementMapTtasCriteria>({
      query: ({mappingFileId, companyId, file, id, objectDocumentAssocId}) => {
        const formData = new FormData()
        formData.append("mappingFileId", mappingFileId)
        if (file) {
          formData.append("file", file)
        }
        if (objectDocumentAssocId) {
          formData.append("objectDocumentAssocId", objectDocumentAssocId)
        }
        return ({
          url: `/company-settlements/${id}/tta-map`,
          method: 'POST',
          body: formData,
          formData: true,
        })
      },
    }),
    createCompanySettlement: builder.mutation<CompanySettlement, Partial<CompanySettlement>>({
      query: (arg) => ({
        url: '/company-settlements',
        method: 'POST',
        body: arg,
      }),
      invalidatesTags: (result, error, {settlementId}) => [
        {type: 'CompanySettlements', id: 'LIST'},
        {type: 'SettlementCompanySettlements', id: settlementId}
      ],
    }),
    updateCompanySettlement: builder.mutation<CompanySettlement, Partial<CompanySettlement>>({
      query: (arg) => ({
        url: '/company-settlements',
        method: 'PUT',
        body: arg,
      }),
      invalidatesTags: (result, error, {id, settlementId}) => [
        {type: 'CompanySettlements', id},
        {type: 'SettlementCompanySettlements', id: settlementId}
      ],
    }),
    deleteCompanySettlement: builder.mutation<void, Partial<CompanySettlement>>({
      query: (arg) => ({
        url: '/company-settlements',
        method: 'DELETE',
        body: arg,
      }),
      invalidatesTags: (result, error, {id, settlementId}) => [
        {type: 'CompanySettlements', id},
        {type: 'SettlementCompanySettlements', id: settlementId}
      ],
    }),
  }),
})

export const {
  useGetAllCompanySettlementsQuery,
  useGetCompanySettlementsBySettlementQuery,
  useGetCompanySettlementQuery,
  useLazyMapCompanySettlementTtasQuery,
  useCreateCompanySettlementMutation,
  useUpdateCompanySettlementMutation,
  useDeleteCompanySettlementMutation,
} = companySettlementApi;
